import { ModalProps, createUseModal, useModal } from "@imminently/react/context/modals";
import {
  Dialog,
  DialogTitle,
  DialogContent
} from "@imminently/react/ui/core/dialog";
import { Text } from "@imminently/react/ui/core/text";
import { Button, TextField } from "@mui/material";
import { ListIcon, ListTreeIcon, SearchIcon } from "lucide-react";
import { ComplaintCodeTree } from "@/components/ComplaintCode/ComplaintCodeTree";
import { ComplaintCodeList } from "@/components/ComplaintCode/ComplaintCodeList";
import { ComplaintCodeProvider, useComplaintCode, useComplaintCodeContext } from "@/components/ComplaintCode/ComplaintCodeContext";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TabsContent } from "@radix-ui/react-tabs";

const slotProps = {
  input: {
    startAdornment: <SearchIcon />
  }
};

enum TabNames {
  TREE = "Tree view",
  LIST = "List view",
}

const TabIcon = ({ tabName }: { tabName: TabNames }) => {
  switch (tabName) {
    case TabNames.TREE:
      return <ListTreeIcon />;
    case TabNames.LIST:
      return <ListIcon />;
    default:
      return null;
  }
};

const ComplaintCodeContent = () => {
  const { setValue } = useComplaintCodeContext();
  return (
    <Tabs defaultValue={TabNames.TREE} className="gap-2">
      <TabsList>
        {Object.values(TabNames).map((t, index) => (
          <TabsTrigger key={index} value={t}>
            <TabIcon tabName={t as TabNames} />
            {t}
          </TabsTrigger>
        ))}
      </TabsList>
      <TextField fullWidth slotProps={slotProps} label="" placeholder="Search..." onChange={(e) => setValue(e.target.value)} autoFocus />
      <TabsContent value={TabNames.TREE}>
        <ComplaintCodeTree />
      </TabsContent>
      <TabsContent value={TabNames.LIST}>
        <ComplaintCodeList />
      </TabsContent>
    </Tabs>
  );
};

interface AddComplaintCodeModalProps {
  onAdd?: (code: string) => void;
}

const AddComplaintCode = ({ props }: ModalProps<AddComplaintCodeModalProps>) => {
  const { popModal: close } = useModal();
  const { onAdd } = props;
  const context = useComplaintCode();
  const { selected } = context;

  const handleAdd = () => {
    if (!selected) return;
    onAdd?.(selected.id);
    close();
  };

  return (
    <Dialog open onClose={close} maxWidth="sm">
      <DialogTitle sx={{ mb: 3, px: 2 }}>
        <Text variant="h3">Add Complaint Code</Text>
        <div className="flex flex-row gap-2">
          <Button variant="outlined" onClick={close}>Cancel</Button>
          <Button disabled={!selected} onClick={handleAdd}>Add</Button>
        </div>
      </DialogTitle>
      <DialogContent sx={{ width: "34rem", pt: 0, pb: 2, px: 2 }}>
        <ComplaintCodeProvider value={context}>
          <ComplaintCodeContent />
        </ComplaintCodeProvider>
      </DialogContent>
    </Dialog>
  );
};

export const useAddComplaintCode = createUseModal(AddComplaintCode);
